/**
 * pinnedNote — select the single note that pins to the top of the player
 * preview drawer (SE-5494).
 *
 * Product rule (supersedes the earlier draft, 2026-07-16): the drawer pins the
 * SINGLE most recent note above the Pipeline-stage section, with **no time
 * window**. An earlier iteration of the spec gated the pin on a 7-day
 * recency cutoff; that rule was explicitly withdrawn, so a note from two years
 * ago still pins. The only reason nothing pins is that the player has no
 * visible notes at all — and in that case the drawer renders neither the pinned
 * note nor a "View all notes" link (AC2).
 *
 * Ordering is resolved HERE rather than trusted from the server. The
 * `NotesListNotesByPlayerId` document
 * (`src/features/notes/operations.graphql:33`) encodes no `orderBy` argument,
 * so `edges[0]` is newest-first only by undocumented resolver convention.
 * Empirically (dev, 2026-07-28, `listNotesByPlayerId` for a player with 77
 * notes) the connection *does* come back in strictly descending `createdAt`
 * order — but an unstated invariant is not a contract, so this module sorts
 * defensively. If the server order is already correct the sort is a no-op; if
 * it ever changes, the drawer keeps pinning the right note.
 * @module features/player-kanban/utils/pinnedNote
 */

/**
 * The minimum note shape the pin selection needs. Deliberately structural (not
 * the generated `NotesNoteFragment`) so callers holding a projection, a test
 * fixture, or a partial cache read can be type-checked against the seam
 * instead of casting.
 */
export interface PinnableNote {
  /** Note id. */
  readonly id: string;
  /** ISO timestamp the note was created. */
  readonly createdAt?: string | null;
}

/**
 * Parse a note's `createdAt` into a sortable epoch.
 * @remarks
 * A missing or unparseable timestamp sorts LAST (`-Infinity`) rather than
 * throwing or being dropped, so a malformed note can never outrank a valid
 * one — but is still eligible to pin when it is the only note the player has.
 * @param note - The note to read.
 * @returns Epoch milliseconds, or `-Infinity` when unknown.
 */
const createdAtEpoch = (note: PinnableNote): number => {
  if (!note.createdAt) return Number.NEGATIVE_INFINITY;
  const parsed = Date.parse(note.createdAt);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
};

/**
 * Select the single most recent note to pin at the top of the drawer.
 * @remarks
 * No recency window is applied — see the module note. Ties on `createdAt`
 * resolve to the note that appeared EARLIER in the input, which preserves the
 * server's own newest-first ordering for same-instant notes instead of
 * reshuffling them arbitrarily.
 * @param notes - The player's visible notes, in any order.
 * @returns The note to pin, or `undefined` when the player has no notes.
 */
export const selectPinnedNote = <TNote extends PinnableNote>(
  notes: readonly TNote[] | null | undefined
): TNote | undefined => {
  if (!notes?.length) return undefined;

  return notes.reduce((newest, candidate) =>
    // Strictly greater, so an equal timestamp keeps the earlier-listed note.
    createdAtEpoch(candidate) > createdAtEpoch(newest) ? candidate : newest
  );
};
